/**
 * Function that manages URL search parameters for category and language selection.
 * It provides reactive references for the selected category and language, and synchronizes them
 * with the URL search parameters.
 * This does not allow for setting the URL search parameters directly.
 *
 * @returns {UseSearchParams} An object containing:
 * - `selectedCategory`: A reactive reference to the selected news category.
 * - `selectedLang`: A reactive reference to the selected language or languages.
 *
 * @remarks
 * - The `category` parameter defaults to `'News'` if it is invalid or not provided.
 * - The `lang` parameter can be a string or an array of strings,
 *   but only the first value is used if it is an array.
 */
import type { Ref } from 'vue'
import { useUrlSearchParams } from '@vueuse/core'
import {
  searchParamsNewsCategory,
  type SearchParamsNewsCategory,
} from '../../utils/newsCategoryTab'

interface UseSearchParams {
  selectedCategory: Ref<SearchParamsNewsCategory>
  selectedLang: Ref<string | string[] | undefined>
}

type UrlParams = {
  category: string
  lang: string | string[] | undefined
}

export function useSearchParams(
  defaultCategory: SearchParamsNewsCategory = 'News'
): UseSearchParams {
  const urlParams = useUrlSearchParams<UrlParams>('history', {
    initialValue: { category: defaultCategory, lang: undefined },
  })

  onMounted(() => {
    const mounted = searchParamsNewsCategory.parse(urlParams.category)
    if (mounted !== urlParams.category) {
      urlParams.category = defaultCategory
    }
  })

  const parsed = searchParamsNewsCategory.parse(urlParams.category)
  const selectedCategory = ref<SearchParamsNewsCategory>(
    parsed === urlParams.category ? parsed : defaultCategory
  )

  watch(selectedCategory, (newValue) => {
    urlParams.category = newValue
  })

  const selectedLang = ref(urlParams.lang)

  watch(
    selectedLang,
    (newValue) => {
      if (Array.isArray(newValue)) {
        urlParams.lang = newValue[0]
      } else {
        urlParams.lang = newValue
      }
    },
    { immediate: true }
  )

  return { selectedCategory, selectedLang }
}
